import { useGetOfferLetterDetailsQuery } from '@/app/features/recruitment/hiring-pipeline/hiring-pipeline.api';
import CustomDrawer from '@/components/CustomDrawer';
import WithLoader from '@/components/WithLoader';
import { PaperclipIcon } from '@/icons';
import { cn } from '@/lib/cn';
import { Tag, Typography } from 'antd';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface OfferLetterDrawerProps {
  open: boolean;
  onClose: () => void;
  offerLetterId: string | null;
}

export default function OfferLetterDrawer({
  open,
  onClose,
  offerLetterId,
}: OfferLetterDrawerProps) {
  const { data, isLoading, isFetching } = useGetOfferLetterDetailsQuery(
    offerLetterId as string,
    {
      skip: !offerLetterId,
    },
  );

  const offerContent = data?.data;

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      width={700}
      title="Offer Letter Details"
    >
      <WithLoader isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}>
        {offerContent && (
          <div>
            {/* Info Grid */}
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-1">
                <Text className="text-theme-slate-400 text-base font-semibold block">
                  Title
                </Text>
                <Text className="text-white text-sm">
                  {offerContent.offer_letter_title}
                </Text>
              </div>
              <div className="space-y-1">
                <Text className="text-theme-slate-400 text-base font-semibold block">
                  Date
                </Text>
                <Text className="text-white text-sm">
                  {dayjs(offerContent.created_at).format('YYYY/MM/DD')}
                </Text>
              </div>
              <div className="space-y-1">
                <Text className="text-theme-slate-400 text-base font-semibold block">
                  Position
                </Text>
                <Text className="text-white text-sm">
                  {offerContent.job_title}
                </Text>
              </div>
            </div>

            {/* Content */}
            <div className="pt-6">
              {/* ql-snow + ql-editor ensures Quill's own CSS (headings, bold, lists, links)
                  is applied to the stored HTML without needing a live editor instance */}
              <div
                className={cn(
                  'ql-snow ql-editor',
                  '!p-0 !bg-transparent !text-white !border-none',
                  'leading-relaxed text-[15px]',
                )}
                dangerouslySetInnerHTML={{
                  __html: offerContent.offer_letter_body,
                }}
              />
            </div>

            {/* Attachment */}
            {offerContent.email_attachment && (
              <div className="pt-6 space-y-2">
                <Text className="text-theme-slate-400 text-sm font-semibold block">
                  Attachment
                </Text>
                <a
                  href={offerContent.email_attachment}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="flex items-center gap-3 w-fit px-4 py-2.5 rounded-lg bg-slate-800 border border-gray-700 hover:border-theme-primary hover:bg-slate-700 transition-all duration-200 group no-underline"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-theme-primary/10">
                    <PaperclipIcon size={16} className="text-theme-primary" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white text-sm font-medium group-hover:text-theme-primary transition-colors duration-200">
                      {offerContent.email_attachment.split('/').pop() ??
                        'attachment.pdf'}
                    </span>
                    <span className="text-gray-500 text-xs">
                      Click to download
                    </span>
                  </div>
                </a>
              </div>
            )}

            {/* Footer / Status */}
            <div className="pt-8 border-t border-[#1a1a1a] space-y-3">
              <Title level={5} className="!text-white !mb-0 font-semibold">
                Offer Letter Status
              </Title>
              <Tag
                className={cn(
                  'px-4 py-1 text-sm border-none rounded',
                  offerContent.offer_letter_status === 'accepted'
                    ? 'bg-green-500/10 text-green-500'
                    : offerContent.offer_letter_status === 'pending'
                      ? 'bg-orange-500/10 text-orange-500'
                      : 'bg-red-500/10 text-red-500',
                )}
              >
                {offerContent.offer_letter_status.charAt(0).toUpperCase() +
                  offerContent.offer_letter_status.slice(1)}
              </Tag>
            </div>
          </div>
        )}
      </WithLoader>
    </CustomDrawer>
  );
}
